import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Avatar, Card, Input, Row, Col, Typography, Empty } from 'antd';
import { TeamOutlined, SearchOutlined, RightOutlined } from '@ant-design/icons';

const { Text } = Typography;

// Search appears once the list is long enough to need it, matching the tag-claim
// picker. Below this a search box is just clutter.
const SEARCH_THRESHOLD = 5;

/**
 * Multi-token contains, ported from the iOS All Teams screen: case-insensitive,
 * and every token has to hit some word. So "front house" finds "Front of House",
 * which a plain substring test would miss.
 */
export function fuzzyMatchTeam(team, query) {
  const tokens = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return true;

  const words = `${team.teamName || ''} ${team.teamDescription || ''}`
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean);

  return tokens.every((token) => words.some((word) => word.includes(token)));
}

/**
 * How many people are on the team right now.
 *
 * `teamNumberOfMembers` is ALREADY net of departures. The onUpdateTeamMember
 * cascade decrements it and increments teamNumberOfDeletedMembers on the same
 * soft delete, and reverses both on restore — the two are separate tallies, not
 * a total and a portion of it.
 *
 * This used to subtract the deleted count, which removed everyone who had ever
 * left a second time. A 16-person team that had churned 15 people over the years
 * displayed as "1 member". Add the two for a total-ever figure if you want one;
 * never subtract.
 *
 * Math.max guards a counter that has drifted below zero rather than rendering a
 * negative — it does not correct one.
 */
export const activeMemberCount = (team) => Math.max((team && team.teamNumberOfMembers) || 0, 0);

/**
 * One team as a row: logo, name, member count. Rows scan far better than a wide
 * grid of tiles when the content is a name and a number, which is why the claim
 * picker reads well and the dashboard grid does not.
 */
export const TeamRowCard = ({ team, to, onClick }) => {
  const members = activeMemberCount(team);

  const card = (
    <Card
      hoverable
      onClick={onClick}
      style={{ borderRadius: 12, border: '1px solid #e8e8e8', cursor: 'pointer' }}
      bodyStyle={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 18px' }}
    >
      <Avatar
        src={team.topLogo || null}
        icon={<TeamOutlined />}
        size={48}
        shape="square"
        style={{ flexShrink: 0, borderRadius: 10, backgroundColor: '#fafafa', color: '#8c8c8c' }}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 16 }} className="text-truncate">
          {team.teamName || 'Untitled team'}
        </div>
        <Text type="secondary" style={{ fontSize: 13 }}>
          {members === 1 ? '1 member' : `${members} members`}
        </Text>
      </div>
      <RightOutlined style={{ color: '#bfbfbf', fontSize: 12, flexShrink: 0 }} />
    </Card>
  );

  return to ? <Link to={to} style={{ display: 'block' }}>{card}</Link> : card;
};

/**
 * A searchable list of teams. One per row on mobile, two up on desktop.
 */
export const TeamRowList = ({ teams, linkFor, onSelect, emptyText = 'No teams yet' }) => {
  const [query, setQuery] = useState('');
  const list = teams || [];

  const filtered = useMemo(
    () => (query.trim() ? list.filter((team) => fuzzyMatchTeam(team, query.trim())) : list),
    [list, query],
  );

  return (
    <>
      {list.length > SEARCH_THRESHOLD && (
        <Input
          prefix={<SearchOutlined style={{ color: '#bbb' }} />}
          placeholder="Search teams…"
          allowClear
          size="large"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={{ marginBottom: 16, maxWidth: 400, borderRadius: 8 }}
        />
      )}

      {filtered.length === 0 ? (
        <Empty description={query.trim() ? `No teams match “${query.trim()}”` : emptyText} />
      ) : (
        <Row gutter={[12, 12]}>
          {filtered.map((team) => (
            <Col key={team.teamId} xs={24} lg={12}>
              <TeamRowCard
                team={team}
                to={linkFor ? linkFor(team) : undefined}
                onClick={onSelect ? () => onSelect(team) : undefined}
              />
            </Col>
          ))}
        </Row>
      )}
    </>
  );
};

export default TeamRowList;
